import { db } from "../firebase";
import {
  collection,
  setDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  query,
  where,
} from "firebase/firestore";

/**
 * Check if a user is registered as a renter.
 * Returns { isRegistered: boolean, userId: string | null }
 */
export const checkTenantRegistration = async (email) => {
  const usersRef = collection(db, "users");
  const userQuery = query(
    usersRef,
    where("email", "==", email),
    where("userType", "==", "renter")
  );
  const userSnapshot = await getDocs(userQuery);
  if (userSnapshot.empty) {
    return { isRegistered: false, userId: null };
  }
  return { isRegistered: true, userId: userSnapshot.docs[0].id };
};

/**
 * Add a tenant to a property.
 * No longer throws if the tenant is unregistered — instead stores isRegistered: false
 * so the email layer can send an onboarding invite alongside the access code.
 */
// tenantService.js — only addTenant changes; all other exports are identical.
// Add lockId to the Firestore write so the first-login handler can find it.

export const addTenant = async (propertyId, tenantData) => {
  if (!propertyId) throw new Error("PropertyId is required to add a tenant");

  const { isRegistered } = await checkTenantRegistration(tenantData.email);

  const tenantDocRef = doc(
    db,
    "access_users",
    propertyId,
    "tenant-emails",
    tenantData.email,
  );

  const existing = await getDoc(tenantDocRef);
  if (existing.exists()) {
    throw new Error(
      `A tenant with email ${tenantData.email} is already added to this property.`
    );
  }

  await setDoc(tenantDocRef, {
    propertyId,
    email: tenantData.email,
    leaseStartDate: tenantData.leaseStartDate,
    leaseEndDate: tenantData.leaseEndDate,
    lockId: tenantData.lockId || null,       // ← stored so first-login can use it
    userType: "tenant",
    status: tenantData.status ?? true,
    reason: tenantData.reason || "",
    isRegistered,
    codeIssued: isRegistered ? false : false, // will be set true after code is sent
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    id: tenantData.email,
    propertyId,
    email: tenantData.email,
    leaseStartDate: tenantData.leaseStartDate,
    leaseEndDate: tenantData.leaseEndDate,
    lockId: tenantData.lockId || null,
    userType: "tenant",
    status: tenantData.status ?? true,
    reason: tenantData.reason || "",
    isRegistered,
    codeIssued: false,
  };
};

export const addAuthorizedUser = async (propertyId, email, addedBy) => {
  if (!propertyId) throw new Error("PropertyId is required");
  if (!email) throw new Error("Email is required");
  if (!addedBy) throw new Error("addedBy (tenant email) is required");

  const userDocRef = doc(
    db,
    "access_users",
    propertyId,
    "authorised_users",
    email,
  );

  const existing = await getDoc(userDocRef);
  if (existing.exists()) {
    throw new Error(`${email} is already added to this property.`);
  }

  await setDoc(userDocRef, {
    propertyId,
    email,
    addedBy,
    userType: "user",
    status: false,
    reason: "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return { id: email, propertyId, email, addedBy, userType: "user", status: false };
};

export const getTenants = async (propertyId) => {
  if (!propertyId) return [];
  const tenantsCollectionRef = collection(
    db,
    "access_users",
    propertyId,
    "tenant-emails",
  );
  const snapshot = await getDocs(tenantsCollectionRef);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getAuthorisedUsers = async (propertyId) => {
  if (!propertyId) return [];
  const snap = await getDocs(
    collection(db, "access_users", propertyId, "authorised_users"),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const deleteAuthorisedUser = async (propertyId, email) => {
  if (!propertyId || !email) throw new Error("PropertyId and email are required");
  await deleteDoc(doc(db, "access_users", propertyId, "authorised_users", email));
  return true;
};

export const updateTenant = async (propertyId, tenantEmail, tenantData) => {
  const tenantRef = doc(
    db,
    "access_users",
    propertyId,
    "tenant-emails",
    tenantEmail,
  );
  await updateDoc(tenantRef, { ...tenantData, updatedAt: serverTimestamp() });
  return { id: tenantEmail, propertyId, ...tenantData };
};

export const approveAuthorisedUser = async (propertyId, email) => {
  if (!propertyId || !email) throw new Error("PropertyId and email are required");
  const ref = doc(db, "access_users", propertyId, "authorised_users", email);
  await updateDoc(ref, { status: true, reason: "", updatedAt: serverTimestamp() });
  return true;
};

export const denyAuthorisedUser = async (propertyId, email, reason) => {
  if (!propertyId || !email) throw new Error("PropertyId and email are required");
  const ref = doc(db, "access_users", propertyId, "authorised_users", email);
  await updateDoc(ref, { status: false, reason: reason || "", updatedAt: serverTimestamp() });
  return true;
};

export const deleteTenant = async (propertyId, tenantEmail) => {
  const tenantRef = doc(
    db,
    "access_users",
    propertyId,
    "tenant-emails",
    tenantEmail,
  );
  await deleteDoc(tenantRef);
  return true;
};